// Sprint 4A.6 — Dirty form guard (no DataRouter required).
// Intercetta:
//  1) Click su <a href> che portano fuori dal baseUrl → mostra modale
//  2) Browser close/refresh tramite beforeunload
//  3) Navigazione programmatica tramite `requestNavigate(url)` esposto dal hook
//     (usare al posto di `navigate(url)` per pulsanti tipo "Torna indietro")
//
// Nota: history.push/pop nativi non sono intercettati: se un componente chiama
// `navigate(...)` direttamente, il guard non scatta. Usare `requestNavigate`.
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { AlertTriangle } from "lucide-react";

/**
 * @param {object} opts
 * @param {boolean} opts.isDirty - true se esistono modifiche non salvate
 * @param {() => Promise<void>|void} opts.onSave - callback di salvataggio
 * @param {string} [opts.baseUrl] - prefisso URL considerato navigazione interna (no prompt)
 */
export function useDirtyGuard({ isDirty, onSave, baseUrl }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingUrl, setPendingUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;

  // Browser close/refresh/external nav
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Intercept <a> clicks that navigate outside baseUrl
  useEffect(() => {
    if (!isDirty) return undefined;
    const currentPath = location.pathname;
    const onDocClick = (e) => {
      if (!dirtyRef.current) return;
      // Look for the nearest <a> ancestor
      const target = e.target.closest && e.target.closest("a[href]");
      if (!target) return;
      const href = target.getAttribute("href");
      if (!href) return;
      // Ignore external/anchor/mailto/tel
      if (href.startsWith("http") || href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("#")) return;
      // Ignore new-tab
      if (target.target && target.target === "_blank") return;
      // Compute final URL
      const finalUrl = href.startsWith("/") ? href : href;
      if (finalUrl === currentPath) return;
      // Internal base URL navigation → allow
      if (baseUrl && finalUrl.startsWith(baseUrl) && currentPath.startsWith(baseUrl)) return;
      // Block and prompt
      e.preventDefault();
      e.stopPropagation();
      setPendingUrl(finalUrl);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [isDirty, baseUrl, location.pathname]);

  const cancel = useCallback(() => setPendingUrl(null), []);
  const discard = useCallback(() => {
    const u = pendingUrl;
    setPendingUrl(null);
    if (u) navigate(u);
  }, [navigate, pendingUrl]);
  const saveAndContinue = useCallback(async () => {
    setSaving(true);
    try {
      await onSave?.();
      const u = pendingUrl;
      setPendingUrl(null);
      if (u) navigate(u);
    } catch {
      // lascia l'utente nella modale se save fallisce
    } finally {
      setSaving(false);
    }
  }, [onSave, pendingUrl, navigate]);

  // Navigazione programmatica "cosciente del dirty".
  // Usare al posto di navigate() nei pulsanti tipo "Torna indietro".
  // Se isDirty → apre la modale; altrimenti naviga subito.
  // Accetta anche opzioni di react-router (es. { replace: true }).
  const requestNavigate = useCallback((to, opts) => {
    if (!to) return;
    if (dirtyRef.current) {
      // Se è una navigazione verso il baseUrl interno, non bloccare.
      const toStr = typeof to === "string" ? to : "";
      if (baseUrl && toStr && toStr.startsWith(baseUrl)) {
        navigate(to, opts);
        return;
      }
      setPendingUrl(toStr || "/");
      return;
    }
    navigate(to, opts);
  }, [navigate, baseUrl]);

  const GuardModal = () => (
    <Dialog open={!!pendingUrl} onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent className="sm:max-w-md" data-testid="dirty-guard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Modifiche non salvate
          </DialogTitle>
          <DialogDescription>
            Hai modifiche non salvate in questa pagina. Cosa vuoi fare prima di uscire?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={cancel}
            data-testid="dirty-guard-cancel"
          >
            Annulla
          </Button>
          <Button
            variant="outline"
            onClick={discard}
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            data-testid="dirty-guard-discard"
          >
            Esci senza salvare
          </Button>
          <Button
            onClick={saveAndContinue}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="dirty-guard-save"
          >
            {saving ? "Salvataggio..." : "Salva e continua"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { GuardModal, requestNavigate };
}
